import * as PropTypes from 'prop-types';

import {assert} from 'Asserts';
import {Owner, Permissions} from 'auth/PermissionConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getRegistrySupportedOligoAwareTypes} from 'constants/RegistryConstants';
import {OwnableInfo} from 'create-components/OwnableConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import {typeToName} from 'util/NameUtil';
import {MaybePromise} from 'util/Promises';

export enum WorklistType {
  CONTAINER = 'container',
  BATCH = 'batch',
  BIOENTITY = 'bioentity',
  PLATE = 'plate',
}

export function asWorklistType(wt: string): WorklistType {
  assert(Object.values(WorklistType).includes(wt as WorklistType), `${wt} is not a WorklistType`);
  return wt as WorklistType;
}

export const WorklistTypeToModelTypeConstant: {[key in WorklistType]: string} = {
  [WorklistType.CONTAINER]: ModelTypeConstants.CONTAINER,
  [WorklistType.BIOENTITY]: ModelTypeConstants.BIOENTITY_MIXIN,
  [WorklistType.BATCH]: ModelTypeConstants.BATCH,
  [WorklistType.PLATE]: ModelTypeConstants.PLATE,
};

export const WORKLIST_TYPE_TO_ICON: {[key in WorklistType]: BenchlingIconName} = {
  [WorklistType.CONTAINER]: ModelTypeConstants.CONTAINER,
  [WorklistType.BIOENTITY]: FolderItemIcon.BasicFolderItem,
  [WorklistType.BATCH]: FolderItemIcon.Material,
  [WorklistType.PLATE]: ModelTypeConstants.PLATE,
};

export function worklistTypeDisplayName(t: string): string {
  return typeToName(WorklistTypeToModelTypeConstant[asWorklistType(t)]);
}

const displayNames = Object.keys(WorklistTypeToModelTypeConstant).map((t: WorklistType) =>
  worklistTypeDisplayName(t)
);

export const mixedWorklistTypeError = `Items must all be of the same type: ${displayNames
  .slice(0, -1)
  .join(', ')}, or ${displayNames[displayNames.length - 1]}`;

export interface WorklistSummary {
  // Worklists are never archived, but may be used in collections of archivable items.
  archiveRecord?: undefined;
  id: string;
  modifiedAt: string;
  name: string;
  itemType: WorklistType;
  permissions: Permissions;
  ownerJson: Owner;
}

export const WORKLIST_SUMMARY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  itemType: PropTypes.oneOf(['container', 'batch', 'bioentity']).isRequired,
});

export interface Worklist {
  id: string;
  modifiedAt: string;
  name: string;
  itemType: WorklistType;
  permissions: Permissions;
  ownerJson: Owner;
  itemIds: Array<string>;
  archivedItemIds: Array<string>;
  inaccessibleItemIds: Array<string>;
}

export type WorklistWithOwnerAndCollaborators = Worklist & OwnableInfo;

export interface WorklistSummariesById {
  [worklistId: string]: WorklistSummary;
}

export const MAX_RECENT_WORKLISTS_TO_DISPLAY = 10;

export const WorklistTypeToModelTypes = {
  [WorklistType.CONTAINER]: [ModelTypeConstants.CONTAINER],
  [WorklistType.BATCH]: [ModelTypeConstants.BATCH],
  [WorklistType.BIOENTITY]: getRegistrySupportedOligoAwareTypes(),
  [WorklistType.PLATE]: [ModelTypeConstants.PLATE],
};

// Keep in sync with Worklist.MAX_NAME_LENGTH in worklists/models/worklist.py
export const MAX_NAME_LENGTH = 255;

export enum WORKLIST_FILL_LOCATION {
  // TODO(Jeff) - The following two constants are not in use yet. They are added in anticipation of
  //  upcoming tasks in epic #10670
  REGISTRATION_TABLE = 'REGISTRATION_TABLE',
  RUN_FIELD = 'RUN_FIELD',
  STANDALONE_BIOENTITY_RESULTS_TABLE = 'STANDALONE_BIOENTITY_RESULTS_TABLE',
  STANDALONE_BATCH_RESULTS_TABLE = 'STANDALONE_BATCH_RESULTS_TABLE',
  TRANSFER_TABLE = 'TRANSFER_TABLE',
  BULK_ASSEMBLY = 'BULK_ASSEMBLY',
  PROCESS_TASK_TABLE = 'PROCESS_TASK_TABLE',
  PROCESS_OUTPUT_TABLE = 'PROCESS_OUTPUT_TABLE',
  LOOKUP_TABLE = 'LOOKUP_TABLE',
  INVENTORY_TABLE = 'INVENTORY_TABLE',
  PLATE_CREATION_TABLE = 'PLATE_CREATION_TABLE',
  BOX_CREATION_TABLE = 'BOX_CREATION_TABLE',
}

export enum WorklistFormType {
  NEW = 'new',
  EXISTING = 'existing',
}

interface ValidationSuccess {
  invalidItemIds: [];
  errorMessage: null;
}

interface ValidationError {
  invalidItemIds: Array<string>;
  errorMessage: string;
}

export type ValidateItemsResponse = ValidationSuccess | ValidationError;

export type ValidateWorklistItems = (itemIds: Array<string>) => MaybePromise<ValidateItemsResponse>;
